import { Injectable } from "@angular/core";
import {
	AlertService,
	ApiResponse,
	Member,
	RestService,
	SessionService,
} from "@recore-solutions/nstar-lib";
import { Observable, of, Subject } from "rxjs";
import {
	catchError,
	filter,
	map,
	shareReplay,
	startWith,
	switchMap,
	take,
	tap,
} from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Preferences } from "../shared/interfaces/preferences";

@Injectable({
	providedIn: "root",
})
export class PreferencesService {
	private refreshPreferencesSubject: Subject<void> = new Subject();
	preferenceRow$: Observable<Member>;
	preference$: Observable<Preferences>;

	constructor(
		private restService: RestService,
		private alertService: AlertService,
		private sessionService: SessionService
	) {
		this.preferenceRow$ = this.refreshPreferencesSubject.asObservable().pipe(
			startWith(true),
			switchMap(() => this.sessionService.session$.pipe(filter((session) => !!session))),
			switchMap(() => this.getPreferencesApiResponse()),
			shareReplay({ bufferSize: 1, refCount: true })
		);
		this.preference$ = this.refreshPreferencesSubject.asObservable().pipe(
			startWith(true),
			switchMap(() => this.sessionService.session$.pipe(filter((session) => !!session))),
			switchMap(() => this.getPreferences()),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	getPreferencesApiResponse = (): Observable<Member | undefined> => {
		return this.restService
			.get<ApiResponse<Member>>(environment.endpoints.preferences)
			.pipe(map((response) => response.records?.[0]));
	};

	getPreferences(): Observable<Preferences> {
		return this.preferenceRow$.pipe(
			map((member) => member?.preferenceList || "{}"),
			map((preferenceList) => JSON.parse(preferenceList)),
			catchError(this.handleError("Error loading preferences!", {}))
		);
	}

	setPreferences(preferences: Preferences, refresh?: boolean): Observable<any> {
		const preferenceList = JSON.stringify(preferences);
		return this.preferenceRow$.pipe(
			take(1),
			map((current) => ({ ...current, preferenceList })),
			switchMap((row) => this.restService.post(environment.endpoints.preferences, row)),
			tap((_) => (refresh ? this.refreshPreferencesSubject.next() : null))
		);
	}

	getPreference(path: string[]): Observable<any> {
		return this.preference$.pipe(map((preferences) => this.getValueFromObj(preferences, path)));
	}

	setPreference(value: any, path: string[], refresh?: boolean): Observable<any> {
		return this.preference$.pipe(
			take(1),
			map((preferences) => this.setValueOnObj(preferences, path, value)),
			switchMap((preferences) => this.setPreferences(preferences, refresh))
		);
	}

	getValueFromObj(obj, path: string[]): any {
		const newPath = [...path];
		const prop = newPath.shift();
		if (!obj) {
			return obj;
		} else if (newPath.length === 0 || !obj[prop]) {
			return obj[prop];
		} else {
			return this.getValueFromObj(obj[prop], newPath);
		}
	}

	setValueOnObj(obj, path: string[], value: any): any {
		const newObj = { ...obj };
		const newPath = [...path];
		const prop = newPath.shift();
		if (newPath.length === 0) {
			return { ...newObj, [prop]: value };
		} else {
			return { ...newObj, [prop]: this.setValueOnObj(newObj[prop], newPath, value) };
		}
	}

	handleError =
		(message: string, fallback?: any) =>
		(error: any): Observable<any> => {
			this.alertService.alertDanger(message);
			return fallback !== undefined ? of(fallback) : of(error);
		};
}
